import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PerformanceChart from '../../components/PerformanceChart';
import FilterPills from '../../components/FilterPills';
import CreateAssignmentModal from '../../components/CreateAssignmentModal';
import TeacherClassCodesModal from '../../components/TeacherClassCodesModal';
import TeacherStudentProgramModal from '../../components/TeacherStudentProgramModal';
import BulkGoalUploadModal from '../../components/BulkGoalUploadModal';
import HedefAtaModal from '../../components/HedefAtaModal';
import { teacherApi, authApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// ---- Data ----
interface Student {
  id: number | string;
  enrollmentId?: string;      // class_student_enrollments.id — used to approve/deny leave
  leaveRequested?: boolean;   // student has asked to leave this class
  uuid?: string;
  studentCode?: string;   // student_profiles.student_code — the real "Öğrenci ID"
  name: string;
  surname: string;
  school: string;
  grade: string;
  email: string;
  phone: string;
  correct: number;
  wrong: number;
  avg: number;
  time: string;
}
// Class no longer carries a `code` field — there's no single canonical
// "Sınıf Kodu" because join codes are minted in batches via the Kodlar
// drawer (see class_join_codes table). The DB row id is not a code.
interface ClassRoom { id: number | string; institution: string; classCode: string; students: Student[]; cover: string }

function mapBackendClass(c: any, idx: number): ClassRoom {
  const enrollments = c.class_student_enrollments ?? [];
  const students: Student[] = enrollments.map((e: any) => {
    const u = e.users ?? {};
    return {
      id: u.id ?? e.id,
      enrollmentId: e.id != null ? String(e.id) : undefined,
      leaveRequested: !!e.leave_requested_at,
      uuid: u.uuid ?? undefined,
      studentCode: u.student_profiles?.student_code ?? undefined,
      name: u.first_name ?? '',
      surname: u.last_name ?? '',
      school: c.campuses?.institutions?.name ?? '',
      grade: c.grade_levels?.name ?? c.grade_level?.name ?? '',
      email: u.login_credentials?.email ?? '',
      phone: u.login_credentials?.phone ?? '',
      correct: u._stats?.correct ?? 0,
      wrong: u._stats?.wrong ?? 0,
      avg: u._stats?.avg ?? 0,
      time: u._stats?.time ?? '-',
    };
  });
  return {
    id: c.id,
    institution: c.campuses?.institutions?.name ?? c.campuses?.name ?? '',
    classCode: c.name ?? '',
    students,
    cover: `/a-sinif-tiling${(idx % 4) + 1}.png`,
  };
}

// Performance data loaded from API per class
interface ClassPerf { stats: any; best3: any[]; worst3: any[]; weakTopics: any[]; chartData: any[]; questionsPerDay: any[]; domainMin: number | null; domainMax: number | null; kazanimTopics: { best3: any[]; worst3: any[]; weakTopics: any[] }; subjects: { id: number; name: string }[]; subjectId: number | null; students: ClassPerfStudent[] | null }
// Per-student totals in the selected period + ders (GET /teacher/performance `students`).
interface ClassPerfStudent { uuid: string; name: string; surname: string; correct: number; wrong: number; total: number; avg: number }
// B24: class-performance time-range filter (mirrors the student Performansım screen).
type PerfPeriod = 'Bu Hafta' | 'Bu Ay' | 'Tüm Zamanlar';
const PERF_PERIOD_PARAM: Record<PerfPeriod, string> = { 'Bu Hafta': 'week', 'Bu Ay': 'month', 'Tüm Zamanlar': 'all' };

const F = { fontFamily: 'Inter' } as const;
const PersonIcon = () => <svg width="18" height="15" viewBox="0 0 24 20" fill="#2b2b2b"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z" /></svg>;

type View = 'list' | 'detail' | 'student-profile' | 'student-perf' | 'class-perf' | 'student-program';

const TeacherClasses: React.FC = () => {
  const location = useLocation();
  const incomingClassId = (location.state as any)?.classId;
  const [classrooms, setClassrooms] = useState<ClassRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>(incomingClassId ? 'detail' : 'list');
  const [selectedClassId, setSelectedClassId] = useState<number | string>(incomingClassId || 0);
  const [selectedStudentId, setSelectedStudentId] = useState<number | string>(0);
  const [studentQuery, setStudentQuery] = useState(''); // B24: class-detail student search
  const [showAddStudent, setShowAddStudent] = useState(false);
  // "Öğrenci Ekle" — add a student to a class by their personal student code
  // (AP39; the modal used to be an unwired mock).
  const [addStudentCode, setAddStudentCode] = useState('');
  const [addStudentClassId, setAddStudentClassId] = useState<string>('');
  const [addStudentSubmitting, setAddStudentSubmitting] = useState(false);
  const [addStudentError, setAddStudentError] = useState<string | null>(null);
  const [addStudentSuccess, setAddStudentSuccess] = useState<string | null>(null);
  const [showAddClass, setShowAddClass] = useState(false);
  const [copyToast, setCopyToast] = useState(false);
  const [createHwOpen, setCreateHwOpen] = useState(false);
  const [classPerf, setClassPerf] = useState<ClassPerf | null>(null);
  const [loadingPerf, setLoadingPerf] = useState(false);
  const [perfPeriod, setPerfPeriod] = useState<PerfPeriod>('Bu Hafta');
  // Selected subject for the class-performance view. Undefined = let the backend
  // pick the teacher's first (primary) subject; its choice comes back as
  // classPerf.subjectId so the dropdown reflects it.
  const [perfSubjectId, setPerfSubjectId] = useState<number | undefined>(undefined);
  // Goal assignment — components/HedefAtaModal (one form for a student or the class).
  const [goalModal, setGoalModal] = useState<{ type: 'student' | 'class'; targetId: string } | null>(null);
  const [bulkGoalTarget, setBulkGoalTarget] = useState<{ type: 'class' | 'student'; id: string; name: string } | null>(null);
  // Class join code management — opens TeacherClassCodesModal for the chosen class
  const [codesFor, setCodesFor] = useState<{ id: string | number; name: string } | null>(null);
  // Create-class form state. Grade levels come from /public/grades (no auth needed).
  // T17 — the picker only offers grades matching the teacher's school_level
  // (secondary → 5-8, highschool → 9-12 + TYT/AYT); the backend enforces the
  // same rule on POST /teacher/classes.
  const { user } = useAuth();
  const teacherSchoolLevel: 'secondary' | 'highschool' =
    (user as any)?.teacher_profiles?.school_level === 'secondary' ? 'secondary' : 'highschool';
  const [createName, setCreateName] = useState('');
  const [createGradeId, setCreateGradeId] = useState<number | ''>('');
  const [createCapacity, setCreateCapacity] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // "Sınıfa Katıl" — join an existing class as a teacher via a class join code.
  const [showJoinClass, setShowJoinClass] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinSubmitting, setJoinSubmitting] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinSuccess, setJoinSuccess] = useState<string | null>(null);
  const [grades, setGrades] = useState<Array<{ id: number; grade_number: number; name: string }>>([]);
  const [quota, setQuota] = useState<{ class_count: number; class_limit: number; student_count: number; student_limit: number; active_code_count: number; active_code_limit: number } | null>(null);
  // Reload quota whenever the teacher mutates anything that affects it.
  const reloadQuota = () => {
    teacherApi.quota()
      .then((r) => setQuota(r.data))
      .catch(() => setQuota(null));
  };
  useEffect(reloadQuota, []);

  const loadClasses = () => {
    return teacherApi.classes()
      .then((res) => {
        const mapped = (res.data || []).map(mapBackendClass);
        setClassrooms(mapped);
        if (mapped.length > 0 && !selectedClassId) setSelectedClassId(mapped[0].id);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadClasses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pending leave requests, flattened across all classes for the banner.
  const [leaveBusyId, setLeaveBusyId] = useState<string | null>(null);
  // Teacher-initiated student removal — confirm before withdrawing the enrollment.
  const [removeTarget, setRemoveTarget] = useState<{ enrollmentId: string; name: string } | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const pendingLeaves = classrooms.flatMap(c =>
    c.students.filter(s => s.leaveRequested && s.enrollmentId).map(s => ({ classroom: c, student: s }))
  );

  // Opens the add-student modal with fresh state. When triggered from a class
  // detail view the class is preselected; from the list header it starts empty.
  const openAddStudent = (classId?: string | number) => {
    setAddStudentCode('');
    setAddStudentError(null);
    setAddStudentSuccess(null);
    setAddStudentClassId(classId != null ? String(classId) : '');
    setShowAddStudent(true);
  };

  const submitAddStudent = async () => {
    setAddStudentError(null);
    setAddStudentSuccess(null);
    const code = addStudentCode.trim().toUpperCase();
    if (!code) { setAddStudentError('Öğrenci kodu gerekli.'); return; }
    if (!addStudentClassId) { setAddStudentError('Sınıf seçin.'); return; }
    setAddStudentSubmitting(true);
    try {
      const r = await teacherApi.addStudentToClass(addStudentClassId, code);
      setAddStudentSuccess(`${r.data.student.name}, "${r.data.class.name}" sınıfına eklendi.`);
      setAddStudentCode('');
      await loadClasses();
      reloadQuota();
      setTimeout(() => { setShowAddStudent(false); setAddStudentSuccess(null); }, 2200);
    } catch (e: any) {
      setAddStudentError(e?.response?.data?.error ?? e.message ?? 'Öğrenci eklenemedi.');
    } finally {
      setAddStudentSubmitting(false);
    }
  };

  const resolveLeave = async (enrollmentId: string, approve: boolean) => {
    setLeaveBusyId(enrollmentId);
    try {
      if (approve) await teacherApi.approveLeaveRequest(enrollmentId);
      else await teacherApi.denyLeaveRequest(enrollmentId);
      await loadClasses();
    } catch (e) {
      console.error(e);
    } finally {
      setLeaveBusyId(null);
    }
  };

  const doRemoveStudent = async () => {
    if (!removeTarget) return;
    setRemoveBusy(true);
    try {
      await teacherApi.removeStudentFromClass(removeTarget.enrollmentId);
      await loadClasses();
      reloadQuota();
      setRemoveTarget(null);
    } catch (e) {
      console.error(e);
    } finally {
      setRemoveBusy(false);
    }
  };

  // Banner listing every student who asked to leave, with approve/deny actions.
  // Rendered on both the Sınıflarım list and the class-detail view.
  const leaveRequestsBanner = pendingLeaves.length > 0 && (
    <div className="mb-5 rounded-[16px] p-4" style={{ background: '#fff4e0', border: '1.5px solid #f0b34a' }}>
      <p style={{ ...F, fontSize: 15, fontWeight: 700, color: '#b3730a', marginBottom: 10 }}>
        Sınıftan ayrılma istekleri ({pendingLeaves.length})
      </p>
      <div className="space-y-2">
        {pendingLeaves.map(({ classroom, student }) => (
          <div key={student.enrollmentId} className="bg-white rounded-[12px] px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
            style={{ border: '1px solid #f0d9ad' }}>
            <p style={{ ...F, fontSize: 14, color: '#2b2b2b' }}>
              <strong>{student.name} {student.surname}</strong>, <strong>{classroom.classCode}</strong> sınıfından ayrılmak istiyor.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => resolveLeave(student.enrollmentId!, true)}
                disabled={leaveBusyId === student.enrollmentId}
                className="rounded-[10px] px-4 py-2 hover:opacity-90 disabled:opacity-40"
                style={{ background: '#e5599b' }}>
                <span style={{ ...F, fontSize: 13, fontWeight: 700, color: '#fff' }}>İzin Ver</span>
              </button>
              <button
                onClick={() => resolveLeave(student.enrollmentId!, false)}
                disabled={leaveBusyId === student.enrollmentId}
                className="rounded-[10px] px-4 py-2 hover:bg-gray-50 disabled:opacity-40"
                style={{ border: '2px solid #3973bf' }}>
                <span style={{ ...F, fontSize: 13, fontWeight: 700, color: '#3973bf' }}>Reddet</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const cls = classrooms.find(c => c.id === selectedClassId) || classrooms[0] || { id: 0, institution: '', classCode: '', students: [], cover: '/a-sinif-tiling1.png' };
  const student = cls.students.find(s => s.id === selectedStudentId) || cls.students[0] || { id: 0, name: '', surname: '', school: '', grade: '', email: '', phone: '', correct: 0, wrong: 0, avg: 0, time: '-' };

  // Load class performance when entering the class-perf view
  useEffect(() => {
    if (view === 'class-perf' && cls.id) {
      setLoadingPerf(true);
      teacherApi.performance(String(cls.id), PERF_PERIOD_PARAM[perfPeriod], perfSubjectId)
        .then((res) => {
          const d = res.data;
          setClassPerf({
            stats: d.stats ?? { correct: 0, wrong: 0, accuracy_pct: 0 },
            best3: d.best3 ?? [],
            worst3: d.worst3 ?? [],
            weakTopics: d.weakTopics ?? [],
            chartData: d.chartData ?? [],
            questionsPerDay: d.questionsPerDay ?? [],
            domainMin: d.domainMin ?? null,
            domainMax: d.domainMax ?? null,
            kazanimTopics: d.kazanimTopics ?? { best3: [], worst3: [], weakTopics: [] },
            subjects: d.subjects ?? [],
            subjectId: d.subjectId ?? null,
            students: Array.isArray(d.students) ? d.students : null,
          });
        })
        .catch(() => setClassPerf(null))
        .finally(() => setLoadingPerf(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, cls.id, perfPeriod, perfSubjectId]);

  const copyCode = (code: string) => { navigator.clipboard.writeText(code).catch(() => {}); setCopyToast(true); setTimeout(() => setCopyToast(false), 2000); };

  const openGoalModal = (type: 'student' | 'class', targetId: string) => setGoalModal({ type, targetId });

  // Hedef Ata — the shared form. Timed goals are checked against the target
  // students' lesson schedule first; another goal in the slot is refused by the
  // backend, naming whose goal holds it.
  const goalStudent = goalModal?.type === 'student' ? cls.students.find((s) => String(s.id) === goalModal.targetId) : undefined;
  const asPreflight = (list: typeof cls.students) =>
    list.filter((s) => s.uuid).map((s) => ({ uuid: s.uuid!, name: `${s.name} ${s.surname}`.trim() }));
  const goalAssignModal = goalModal && (
    <HedefAtaModal
      open
      target={goalModal.type === 'class'
        ? { kind: 'class', classId: goalModal.targetId, name: cls.classCode || 'Sınıf' }
        : { kind: 'student', uuid: goalStudent?.uuid ?? goalModal.targetId, name: goalStudent ? `${goalStudent.name} ${goalStudent.surname}`.trim() : 'Öğrenci', classId: String(cls.id) }}
      preflightStudents={asPreflight(goalModal.type === 'class' ? cls.students : goalStudent ? [goalStudent] : [])}
      onClose={() => setGoalModal(null)}
      onSubmitted={() => {}}
    />
  );

  const BackBtn: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <button onClick={onClick} className="hover:bg-gray-100 rounded-full p-1 transition-colors">
      <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><path d="M20 8l-8 8 8 8" /></svg>
    </button>
  );

  // ---- Close X (48x48) ----
  const CloseX: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <button onClick={onClick} className="absolute top-5 right-5 w-[48px] h-[48px] rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="4" strokeLinecap="round"><line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" /></svg>
    </button>
  );

  // ---- ADD STUDENT MODAL ----
  // AP39: enrolls a student by their personal code via
  // POST /teacher/classes/:id/students (used to be an unwired mock).
  const addStudentModal = showAddStudent && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowAddStudent(false)} />
      <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[919px] p-10 overflow-hidden">
        <CloseX onClick={() => setShowAddStudent(false)} />
        <div className="flex gap-8">
          <div className="flex-1">
            <div className="mb-6">
              <label style={{ ...F, fontSize: 32, fontWeight: 500, color: '#2b2b2b', display: 'block', marginBottom: 10 }}>Öğrencinizin Kodunu Giriniz</label>
              <div className="flex items-center bg-[#fafafa] rounded-[20px] px-4 h-[79px] gap-4" style={{ border: '2px solid #3973bf' }}>
                <div className="w-[56px] h-[59px] rounded-[20px] bg-[#3973bf] flex items-center justify-center flex-shrink-0"><svg width="25" height="28" viewBox="0 0 24 24" fill="#fafafa"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z" /></svg></div>
                <input
                  placeholder="Örn: 83VNS5RK"
                  value={addStudentCode}
                  onChange={(e) => setAddStudentCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !addStudentSubmitting) submitAddStudent(); }}
                  disabled={addStudentSubmitting}
                  className="flex-1 bg-transparent outline-none text-[24px] font-medium text-[#2b2b2b]"
                  style={{ ...F, letterSpacing: 1 }}
                />
              </div>
              <p style={{ ...F, fontSize: 12, color: '#888', marginTop: 8 }}>
                Öğrencinin kişisel kodu; profilinde ve velisinin uygulamasında görünür.
              </p>
            </div>
            <div className="mb-8">
              <label style={{ ...F, fontSize: 32, fontWeight: 500, color: '#2b2b2b', display: 'block', marginBottom: 10 }}>Sınıf Seçiniz</label>
              <div className="flex items-center bg-[#fafafa] rounded-[20px] px-4 h-[79px] gap-4" style={{ border: '2px solid #e5599b' }}>
                <select
                  value={addStudentClassId}
                  onChange={(e) => setAddStudentClassId(e.target.value)}
                  disabled={addStudentSubmitting}
                  className="flex-1 bg-transparent outline-none text-[24px] font-semibold text-[#2b2b2b] appearance-none cursor-pointer"
                  style={F}
                >
                  <option value="">Sınıf Seç</option>
                  {classrooms.map(c => <option key={c.id} value={String(c.id)}>{c.classCode}</option>)}
                </select>
                <svg width="20" height="12" viewBox="0 0 20 12" fill="#2b2b2b"><path d="M2 2l8 8 8-8" /></svg>
              </div>
            </div>
            {addStudentError && (
              <div className="mb-3 rounded-[10px] px-4 py-2 text-[14px]" style={{ background: '#fdecef', color: '#c73d74' }}>
                {addStudentError}
              </div>
            )}
            {addStudentSuccess && (
              <div className="mb-3 rounded-[10px] px-4 py-2 text-[14px]" style={{ background: '#dff5e0', color: '#1f7a35' }}>
                {addStudentSuccess}
              </div>
            )}
            <button
              onClick={submitAddStudent}
              disabled={addStudentSubmitting || !addStudentCode.trim() || !addStudentClassId}
              className="rounded-[12px] py-4 hover:opacity-90 disabled:opacity-50"
              style={{ width: 460, background: '#3973bf' }}
            >
              <span style={{ ...F, fontSize: 20, fontWeight: 700, color: '#fafafa' }}>
                {addStudentSubmitting ? 'Ekleniyor…' : 'Sınıfa Ekle'}
              </span>
            </button>
          </div>
          <img src="/a-popup-mascot-student.png" alt="" className="hidden lg:block w-[292px] h-[159px] object-contain self-center flex-shrink-0" />
        </div>
      </div>
    </div>
  );

  // ---- CREATE CLASS MODAL ----
  // Lazy-loads the public grades list once when the modal opens. Grades come
  // from /public/grades so the teacher doesn't need an admin call.
  useEffect(() => {
    if (showAddClass && grades.length === 0) {
      authApi.publicGrades?.()
        .then((r: any) => {
          const all: Array<{ id: number; grade_number: number | null; name: string }> = r.data ?? [];
          // T17 — grade_number 5-8 = ortaokul; 9-12 and null (TYT/AYT) = lise.
          const wantSecondary = teacherSchoolLevel === 'secondary';
          setGrades(all.filter((g) => {
            const isSecondary = g.grade_number != null && g.grade_number >= 5 && g.grade_number <= 8;
            return isSecondary === wantSecondary;
          }) as any);
        })
        .catch(() => setGrades([]));
    }
  }, [showAddClass, grades.length, teacherSchoolLevel]);

  const submitCreateClass = async () => {
    setCreateError(null);
    if (!createName.trim()) { setCreateError('Sınıf adı gerekli.'); return; }
    if (!createGradeId) { setCreateError('Sınıf seviyesi seçin.'); return; }
    setCreateSubmitting(true);
    try {
      const r = await teacherApi.createClass({
        name: createName.trim(),
        gradeLevelId: Number(createGradeId),
        capacity: createCapacity ? Number(createCapacity) : undefined,
      });
      // Refresh class list so the new one is visible, then jump straight into
      // its codes manager — the typical next step is "give me a code to share".
      const created = r.data as { id: string; name: string };
      const fresh = await teacherApi.classes();
      setClassrooms((fresh.data || []).map(mapBackendClass));
      setShowAddClass(false);
      setCreateName(''); setCreateGradeId(''); setCreateCapacity('');
      reloadQuota();
      setCodesFor({ id: created.id, name: created.name });
    } catch (e: any) {
      setCreateError(e?.response?.data?.error ?? e.message);
    } finally {
      setCreateSubmitting(false);
    }
  };

  const submitJoinClass = async () => {
    setJoinError(null); setJoinSuccess(null);
    if (!joinCode.trim()) { setJoinError('Sınıf kodu gerekli.'); return; }
    setJoinSubmitting(true);
    try {
      const r = await teacherApi.joinClass(joinCode.trim());
      const fresh = await teacherApi.classes();
      setClassrooms((fresh.data || []).map(mapBackendClass));
      setJoinSuccess(`"${r.data.class.name}" sınıfına öğretmen olarak katıldınız.`);
      setJoinCode('');
      setTimeout(() => { setShowJoinClass(false); setJoinSuccess(null); }, 1600);
    } catch (e: any) {
      setJoinError(e?.response?.data?.error ?? e.message);
    } finally {
      setJoinSubmitting(false);
    }
  };

  const joinClassModal = showJoinClass && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => !joinSubmitting && setShowJoinClass(false)} />
      <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[480px] p-8">
        <CloseX onClick={() => !joinSubmitting && setShowJoinClass(false)} />
        <h2 style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b', marginBottom: 8 }}>Sınıfa Katıl</h2>
        <p style={{ ...F, fontSize: 13, color: '#666', marginBottom: 20 }}>
          Yöneticinizin verdiği sınıf katılım kodunu girin. Branşınıza göre sınıfın öğretmeni olarak eklenirsiniz.
        </p>
        <label className="block mb-5">
          <span style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Sınıf Kodu</span>
          <input
            type="text" placeholder="örn. ABC123XY" value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') submitJoinClass(); }}
            className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[16px] tracking-[0.15em]"
            style={{ border: '2px solid #3973bf', ...F }}
            autoFocus
          />
        </label>
        {joinError && (
          <div className="mb-4 rounded-[10px] px-4 py-2 text-[13px] text-[#c73d74]" style={{ background: '#fdecef' }}>{joinError}</div>
        )}
        {joinSuccess && (
          <div className="mb-4 rounded-[10px] px-4 py-2 text-[13px] text-[#2a7d4f]" style={{ background: '#e8f7ee' }}>{joinSuccess}</div>
        )}
        <div className="flex gap-3">
          <button
            onClick={() => setShowJoinClass(false)} disabled={joinSubmitting}
            className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50" style={{ background: '#e5e5e5' }}
          >
            <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>İptal</span>
          </button>
          <button
            onClick={submitJoinClass} disabled={joinSubmitting}
            className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50" style={{ background: '#3973bf' }}
          >
            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>{joinSubmitting ? 'Katılınıyor…' : 'Sınıfa Katıl'}</span>
          </button>
        </div>
      </div>
    </div>
  );

  const addClassModal = showAddClass && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => !createSubmitting && setShowAddClass(false)} />
      <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[560px] p-8">
        <CloseX onClick={() => !createSubmitting && setShowAddClass(false)} />
        <h2 style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b', marginBottom: 16 }}>Yeni Sınıf Oluştur</h2>
        <p style={{ ...F, fontSize: 13, color: '#666', marginBottom: 20 }}>
          Sınıf oluşturduktan sonra öğrencileriniz için katılım kodu üretebilirsiniz.
        </p>

        <label className="block mb-4">
          <span style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Sınıf Adı</span>
          <input
            type="text" placeholder="örn. 9-A" value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[15px]"
            style={{ border: '2px solid #3973bf', ...F }}
          />
        </label>

        <label className="block mb-4">
          <span style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Sınıf Seviyesi</span>
          <select
            value={createGradeId}
            onChange={(e) => setCreateGradeId(e.target.value ? Number(e.target.value) : '')}
            className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[15px] appearance-none"
            style={{ border: '2px solid #3973bf', ...F }}
          >
            <option value="">Seviye seçin</option>
            {grades.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </label>

        <label className="block mb-5">
          <span style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Kapasite (opsiyonel)</span>
          <input
            type="number" min={1} max={500} placeholder="örn. 30"
            value={createCapacity} onChange={(e) => setCreateCapacity(e.target.value)}
            className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[15px]"
            style={{ border: '2px solid #d8d8d8', ...F }}
          />
        </label>

        {createError && (
          <div className="mb-4 rounded-[10px] px-4 py-2 text-[13px] text-[#c73d74]" style={{ background: '#fdecef' }}>{createError}</div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => setShowAddClass(false)} disabled={createSubmitting}
            className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50" style={{ background: '#e5e5e5' }}
          >
            <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>İptal</span>
          </button>
          <button
            onClick={submitCreateClass} disabled={createSubmitting}
            className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50" style={{ background: '#e5599b' }}
          >
            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>
              {createSubmitting ? 'Oluşturuluyor…' : 'Oluştur ve Kod Üret'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  const toast = copyToast && <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#3973bf] text-white text-[13px] font-semibold px-4 py-2 rounded-[10px] shadow-lg animate-pulse">Kod Kopyalandı!</div>;

  // ---- CLASS PERFORMANCE ----
  if (view === 'class-perf') {
    const best = cls.students.slice().sort((a, b) => b.avg - a.avg).slice(0, 3);
    const worst = cls.students.slice().sort((a, b) => a.avg - b.avg).slice(0, 3);
    return (
      <div>
        {toast}
        <div className="flex items-center gap-3 mb-5">
          <BackBtn onClick={() => setView('detail')} />
          <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Sınıf Performansı - {cls.classCode}</h1>
          <div className="ml-auto flex items-center gap-2">
            {(classPerf?.subjects ?? []).length > 0 && (
              <select
                value={String(perfSubjectId ?? classPerf?.subjectId ?? '')}
                onChange={(e) => setPerfSubjectId(e.target.value ? Number(e.target.value) : undefined)}
                className="rounded-[10px] px-3 py-2 cursor-pointer"
                style={{ ...F, fontSize: 13, fontWeight: 600, background: '#fff', color: '#2b2b2b', border: '1.5px solid #e5e7eb' }}
                title="Ders seç"
              >
                {(classPerf?.subjects ?? []).map((s: { id: number; name: string }) => (
                  <option key={s.id} value={String(s.id)}>{s.name}</option>
                ))}
              </select>
            )}
            <FilterPills<PerfPeriod>
              label="Dönem"
              options={(['Bu Hafta', 'Bu Ay', 'Tüm Zamanlar'] as PerfPeriod[]).map(p => ({ value: p, label: p }))}
              value={perfPeriod}
              onChange={setPerfPeriod}
              tone="blue"
            />
          </div>
        </div>
        {loadingPerf ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <>
            <div className="flex flex-col xl:flex-row gap-5 mb-5">
              <div className="flex-1 bg-[#fafafa] rounded-[24px] p-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                <div className="flex items-center justify-between mb-3">
                  <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>Sınıf Performansı</span>
                  <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>Başarı Puanı (0 - 100)</span>
                </div>
                <div style={{ height: 260 }}>
                  <PerformanceChart
                    data={(classPerf?.chartData ?? []).length > 0 ? classPerf!.chartData : Array.from({ length: 7 }, (_, i) => ({ label: `G${i+1}`, value: 0 }))}
                    color="#3973bf" id="cls-perf" height={260} strokeWidth={5}
                    domainMin={classPerf?.domainMin ?? undefined} domainMax={classPerf?.domainMax ?? undefined} yMax={100}
                  />
                </div>
              </div>
              <div className="w-full xl:w-[380px] xl:flex-shrink-0 bg-[#fafafa] rounded-[16px] overflow-hidden" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                <div className="bg-[#e5599b] px-5 py-4 flex justify-around" style={{ borderRadius: '16px 16px 0 0' }}>
                  {[
                    { l: 'Doğru', v: classPerf?.stats?.correct ?? 0 },
                    { l: 'Yanlış', v: classPerf?.stats?.wrong ?? 0 },
                    { l: 'D. Ort', v: `%${classPerf?.stats?.accuracy_pct ?? 0}` },
                  ].map(s => (
                    <div key={s.l} className="text-center">
                      <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#fff' }}>{s.l}</p>
                      <p style={{ ...F, fontSize: 24, fontWeight: 600, color: '#fafafa' }}>{s.v}</p>
                    </div>
                  ))}
                </div>
                <div className="p-4 space-y-2 max-h-[220px] overflow-y-auto">
                  {(classPerf?.weakTopics ?? []).length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-4">Konu verisi yok.</p>
                  ) : classPerf!.weakTopics.map((w: any, i: number) => (
                    <div key={i} className="bg-[#fafafa] rounded-[12px] flex overflow-hidden" style={{ border: '2px solid #e5599b' }}>
                      <div className="flex-1 px-3 py-2 min-w-0">
                        <p className="truncate" style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b' }}>{w.name ?? w.topic ?? 'Konu'}</p>
                        <p style={{ ...F, fontSize: 11, fontWeight: 500, color: '#888' }}>
                          {w.correct_count ?? 0} doğru · {w.wrong_count ?? w.score ?? 0} yanlış · %{w.accuracy_pct ?? 0}
                        </p>
                      </div>
                      <div className="w-[72px] bg-[#e5599b] flex flex-col items-center justify-center flex-shrink-0" style={{ borderRadius: '0 12px 12px 0' }}>
                        <span style={{ ...F, fontSize: 10, fontWeight: 600, color: '#fff' }}>Yanlış</span>
                        <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#fff' }}>{w.wrong_count ?? w.score ?? 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* B24: Soru Sayısı — questions solved per day (mirrors the student screen) */}
            <div className="bg-[#fafafa] rounded-[24px] p-5 mb-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>Soru Sayısı</span>
              <p style={{ ...F, fontSize: 13, color: '#888', margin: '4px 0 8px' }}>Sınıfın her gün çözdüğü toplam soru adedi.</p>
              <div style={{ height: 260 }}>
                <PerformanceChart
                  data={(classPerf?.questionsPerDay ?? []).length > 0 ? classPerf!.questionsPerDay : Array.from({ length: 7 }, (_, i) => ({ label: `G${i+1}`, value: 0 }))}
                  color="#7c5cff" id="cls-soru" height={260} strokeWidth={5}
                  domainMin={classPerf?.domainMin ?? undefined} domainMax={classPerf?.domainMax ?? undefined}
                  yMax={Math.max(10, Math.ceil(Math.max(0, ...(classPerf?.questionsPerDay ?? []).map((d: any) => d.value ?? 0)) / 10) * 10)}
                />
              </div>
            </div>

            {/* B24: class-wide source-topic success. */}
            <div className="flex flex-col xl:flex-row gap-5 mb-5">
              {[
                { title: 'En İyi Konular', rows: classPerf?.kazanimTopics?.best3 ?? [], accent: '#16a34a' },
                { title: 'Geliştirilecek Konular', rows: classPerf?.kazanimTopics?.weakTopics ?? [], accent: '#e5599b' },
              ].map(sec => (
                <div key={sec.title} className="flex-1 bg-[#fafafa] rounded-[16px] p-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                  <p style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b', marginBottom: 12 }}>{sec.title}</p>
                  {sec.rows.length === 0 ? (
                    <p className="text-gray-400 text-sm py-2">Yeterli veri yok.</p>
                  ) : (
                    <div className="space-y-2">
                      {sec.rows.map((k: any, i: number) => (
                        <div key={i} className="bg-white rounded-[12px] flex overflow-hidden" style={{ border: `2px solid ${sec.accent}` }}>
                          <div className="flex-1 px-3 py-2 min-w-0">
                            <p style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b' }} className="truncate">{k.title ?? 'Konu'}</p>
                            <p style={{ ...F, fontSize: 11, fontWeight: 500, color: '#888' }} className="truncate">{k.subject_name ?? ''}</p>
                          </div>
                          <div className="w-[64px] flex flex-col items-center justify-center flex-shrink-0" style={{ background: sec.accent, borderRadius: '0 10px 10px 0' }}>
                            <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fff' }}>%{k.accuracy_pct ?? 0}</span>
                            <span style={{ ...F, fontSize: 9, fontWeight: 600, color: '#fff' }}>başarı</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Best/worst students — ranked on the SELECTED period + ders (payload
                `students`), like the mobile screen. They used to rank the roster's
                all-time, all-subject _stats, so the lists ignored the pickers above.
                Only students who answered something in the window are ranked; an older
                backend without `students` falls back to _stats. */}
            <div className="flex gap-5 mb-5">
              {(() => {
                const ranked = classPerf?.students
                  ? classPerf.students.filter((s) => s.total > 0).map((s) => ({ id: s.uuid, name: s.name, surname: s.surname, correct: s.correct, wrong: s.wrong, avg: s.avg, total: s.total }))
                  : cls.students.map((s) => ({ id: String(s.id), name: s.name, surname: s.surname, correct: s.correct, wrong: s.wrong, avg: s.avg, total: s.correct + s.wrong }));
                return [
                  { title: 'En İyi 3 Öğrenci', items: ranked.slice().sort((a, b) => b.avg - a.avg || b.total - a.total).slice(0, 3), color: '#3973bf' },
                  { title: 'En Zayıf 3 Öğrenci', items: ranked.slice().sort((a, b) => a.avg - b.avg || b.total - a.total).slice(0, 3), color: '#e5599b' },
                ];
              })().map(sec => (
                <div key={sec.title} className="flex-1">
                  <h2 style={{ ...F, fontSize: 18, fontWeight: 600, color: '#2b2b2b', marginBottom: 10 }}>{sec.title}</h2>
                  <div className="space-y-2">
                    {sec.items.length === 0 ? <p className="text-gray-400 text-sm">Bu dönemde çözülen soru yok.</p> : null}
                    {sec.items.map(s => (
                      <div key={s.id} className="bg-[#fafafa] rounded-[14px] overflow-hidden" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                        <div className="px-4 py-3 flex items-center gap-3">
                          <div className="w-[36px] h-[36px] rounded-full bg-[#3973bf] overflow-hidden flex-shrink-0">
                            <img src="/a-avatar.png" alt="" className="w-full h-full object-cover" />
                          </div>
                          <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>{s.name} {s.surname}</span>
                        </div>
                        <div className="flex justify-around py-2.5" style={{ background: sec.color }}>
                          {[{ l: 'Doğru', v: s.correct }, { l: 'Yanlış', v: s.wrong }, { l: '%Ort', v: `%${s.avg}` }].map(st => (
                            <div key={st.l} className="text-center">
                              <span style={{ ...F, fontSize: 12, fontWeight: 500, color: '#fff' }}>{st.l}: </span>
                              <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#fff' }}>{st.v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Best/Worst tests — matches the student panel's TestCard so the
                teacher's class-perf and student-perf cards read identically. */}
            <div className="flex gap-5">
              {[
                { title: 'En İyi 3 Test', color: '#3973bf', items: classPerf?.best3 ?? [] },
                { title: 'En Zayıf 3 Test', color: '#e5599b', items: classPerf?.worst3 ?? [] },
              ].map(sec => (
                <div key={sec.title} className="flex-1">
                  <h2 style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b', marginBottom: 8 }}>{sec.title}</h2>
                  {sec.items.length === 0 ? (
                    <p className="text-gray-400 text-sm">Veri yok.</p>
                  ) : (
                    <div className="space-y-3">
                      {sec.items.map((t: any, i: number) => (
                        <div
                          key={i}
                          className="rounded-[14px] block w-full"
                          style={{ background: sec.color, padding: 2, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
                        >
                          <div className="flex rounded-[12px] overflow-hidden">
                            <div className="flex-1 p-2.5 bg-[#fafafa]">
                              <p style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b' }}>{t.detail || t.label || 'Test'}</p>
                              <p style={{ ...F, fontSize: 11, fontWeight: 500, color: '#2b2b2b', marginTop: 2 }}>{t.subject || ''}</p>
                            </div>
                            <div className="w-[72px] flex flex-col items-center justify-center flex-shrink-0" style={{ background: sec.color }}>
                              <span style={{ ...F, fontSize: 10, fontWeight: 600, color: '#fff' }}>Doğru</span>
                              <span style={{ ...F, fontSize: 18, fontWeight: 600, color: '#fff' }}>%{Math.round(t.value ?? 0)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // ---- STUDENT PERFORMANCE (identical to student's own performance page) ----
  const StudentPerfView: React.FC = () => {
    const [perfPeriod, setPerfPeriod] = useState<'week' | 'month' | 'all'>('week');
    const [perfData, setPerfData] = useState<any>(null);
    const [perfLoading, setPerfLoading] = useState(true);
    const PERIOD_LABELS: Record<string, string> = { week: 'Bu Hafta', month: 'Bu Ay', all: 'Tüm Zamanlar' };

    useEffect(() => {
      if (!student.uuid) return;
      setPerfLoading(true);
      teacherApi.studentPerformance(student.uuid, perfPeriod)
        .then(res => setPerfData(res.data))
        .catch(console.error)
        .finally(() => setPerfLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [perfPeriod, student.uuid]);

    const chartData: any[] = (perfData?.chartData ?? []).length > 0
      ? perfData.chartData
      : Array.from({ length: 7 }, (_: any, i: number) => ({ label: `G${i + 1}`, value: 0 }));
    const stats = perfData?.stats ?? { correct: student.correct, wrong: student.wrong, accuracy_pct: student.avg };
    const weakTopics: any[] = perfData?.weakTopics ?? [];

    return (
      <div>
        {toast}
        <div className="flex items-center gap-3 mb-5">
          <BackBtn onClick={() => setView('student-profile')} />
          <h1 style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b' }}>
            {student.name} {student.surname} - Performans
          </h1>
          {/* Period selector */}
          <FilterPills<'week' | 'month' | 'all'>
            className="ml-auto"
            label="Dönem"
            options={(['week', 'month', 'all'] as const).map(p => ({ value: p, label: PERIOD_LABELS[p] }))}
            value={perfPeriod}
            onChange={setPerfPeriod}
            tone="blue"
          />
        </div>

        {perfLoading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="flex flex-col xl:flex-row gap-5">
            {/* Left: chart */}
            <div className="flex-1">
              <div className="bg-[#fafafa] rounded-[24px] p-5 mb-4" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                <div className="flex items-center justify-between mb-3">
                  <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#2b2b2b' }}>{PERIOD_LABELS[perfPeriod]}</span>
                  <span style={{ ...F, fontSize: 14, color: '#888' }}>Başarı Puanı (0–100)</span>
                </div>
                <div style={{ height: 260 }}>
                  <PerformanceChart data={chartData} color="#3973bf" id="stu-teacher-perf" height={260} strokeWidth={5}
                    domainMin={perfData?.domainMin ?? undefined} domainMax={perfData?.domainMax ?? undefined} />
                </div>
              </div>
              {/* Best/worst kazanımlar — /teacher/student-performance returns konu
                  (subject_unit) aggregates here, not tests; the mobile report says so too. */}
              <div className="flex gap-4">
                {[
                  { title: 'En İyi 3 Kazanım', color: '#3973bf', items: perfData?.best3 ?? [] },
                  { title: 'En Zayıf 3 Kazanım', color: '#e5599b', items: perfData?.worst3 ?? [] },
                ].map(sec => (
                  <div key={sec.title} className="flex-1">
                    <h3 style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b', marginBottom: 8 }}>{sec.title}</h3>
                    {sec.items.length === 0 ? <p className="text-gray-400 text-sm">Veri yok</p> : (
                      <div className="space-y-2">
                        {sec.items.map((t: any, i: number) => (
                          <div key={i} className="bg-[#fafafa] rounded-[14px] flex overflow-hidden"
                            style={{ border: `2px solid ${sec.color}` }}>
                            <div className="flex-1 px-3 py-2">
                              <p style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b' }}>{t.detail || t.label}</p>
                              <p style={{ ...F, fontSize: 11, color: '#888' }}>{t.subject}</p>
                            </div>
                            <div className="w-[80px] flex flex-col items-center justify-center flex-shrink-0"
                              style={{ background: sec.color, borderRadius: '0 14px 14px 0' }}>
                              <span style={{ ...F, fontSize: 10, color: '#fff' }}>Doğru</span>
                              <span style={{ ...F, fontSize: 18, fontWeight: 700, color: '#fff' }}>%{Math.round(t.value ?? 0)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: stats + weak topics */}
            <div className="w-full xl:w-[320px] xl:flex-shrink-0">
              <div className="bg-[#e5599b] rounded-[16px] px-4 py-4 flex justify-around mb-4" style={{ borderRadius: '16px 16px 0 0' }}>
                {[
                  { l: 'Doğru', v: stats.correct },
                  { l: 'Yanlış', v: stats.wrong },
                  { l: '%Ort', v: `%${stats.accuracy_pct ?? stats.avg ?? 0}` },
                ].map(s => (
                  <div key={s.l} className="text-center">
                    <p style={{ ...F, fontSize: 14, color: '#fff' }}>{s.l}</p>
                    <p style={{ ...F, fontSize: 22, fontWeight: 700, color: '#fff' }}>{s.v}</p>
                  </div>
                ))}
              </div>
              <div className="bg-[#fafafa] rounded-b-[16px] p-3 space-y-2" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                {weakTopics.length === 0
                  ? <p className="text-gray-400 text-sm text-center py-3">Zayıf konu verisi yok.</p>
                  : weakTopics.map((w: any, i: number) => {
                    // This payload's rows are { title, wrong_count, correct_count,
                    // accuracy_pct, subject_units } — there is no name/score, which is
                    // why every row used to read "Konu · 0 yanlış".
                    const wrong = w.wrong_count ?? w.score ?? 0;
                    return (
                    <div key={i} className="bg-white rounded-[10px] flex overflow-hidden" style={{ border: '2px solid #e5599b' }}>
                      <div className="flex-1 px-3 py-2 min-w-0">
                        <p style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b' }}>{w.title ?? w.name ?? w.subject_units?.name ?? 'Kazanım'}</p>
                        <p style={{ ...F, fontSize: 11, color: '#888' }}>
                          {w.correct_count ?? 0} doğru · {wrong} yanlış{w.accuracy_pct != null ? ` · %${w.accuracy_pct}` : ''}
                        </p>
                      </div>
                      <div className="w-[64px] bg-[#e5599b] flex flex-col items-center justify-center flex-shrink-0" style={{ borderRadius: '0 10px 10px 0' }}>
                        <span style={{ ...F, fontSize: 9, color: '#fff' }}>Yanlış</span>
                        <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fff' }}>{wrong}</span>
                      </div>
                    </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const bulkGoalModal = bulkGoalTarget && (
    <BulkGoalUploadModal
      target={bulkGoalTarget}
      onClose={() => setBulkGoalTarget(null)}
      onDone={() => { /* goals apply immediately; nothing to refresh on this list view */ }}
    />
  );

  if (view === 'student-perf') return <StudentPerfView />;

  // Phase 10 — Öğrenci Çalışma Programı page (full sub-view, not modal).
  // Reached via "Programını İncele" button on the student profile.
  if (view === 'student-program') return (
    <div>
      {toast}
      <div className="flex items-center gap-3 mb-5">
        <BackBtn onClick={() => setView('student-profile')} />
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Öğrenci Çalışma Programı</h1>
      </div>
      {student.uuid ? (
        <TeacherStudentProgramModal
          mode="page"
          studentUuid={student.uuid}
          studentName={`${student.name} ${student.surname}`.trim()}
          onClose={() => setView('student-profile')}
        />
      ) : (
        <p className="text-gray-400 text-center py-10">Öğrenci kimliği bulunamadı.</p>
      )}
    </div>
  );

  // ---- STUDENT PROFILE ----
  if (view === 'student-profile') return (
    <div>
      {toast}
      {goalAssignModal}
      {bulkGoalModal}
      <div className="flex items-center gap-3 mb-5">
        <BackBtn onClick={() => setView('detail')} />
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Öğrenci Profili</h1>
      </div>
      <div className="flex flex-col xl:flex-row gap-7">
        <div className="flex-1 min-w-0">
          {/* Info card */}
          <div className="bg-white rounded-[24px] p-5 mb-5">
            <div className="flex items-start gap-5">
              <div className="w-[133px] h-[133px] rounded-full bg-[#3973bf] overflow-hidden flex-shrink-0"><img src="/a-avatar.png" alt="" className="w-full h-full object-cover" /></div>
              <div className="flex-1">
                <p style={{ ...F, fontSize: 32, fontWeight: 600, color: '#2b2b2b', marginBottom: 4 }}>{student.name} {student.surname}</p>
                <p style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>{student.school}</p>
                <p style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>{student.grade}</p>
              </div>
              <div className="flex-shrink-0 flex flex-col items-end gap-3">
                {/* Real student code from student_profiles.student_code, not
                    the misleading class DB id that used to live here. */}
                {student.studentCode && (
                  <>
                    <div>
                      <span style={{ ...F, fontSize: 16, fontWeight: 600, color: '#3973bf' }}>Öğrenci ID: </span>
                      <span style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>{student.studentCode}</span>
                    </div>
                    <button onClick={() => copyCode(student.studentCode!)} className="rounded-[12px] py-3 text-center hover:opacity-90" style={{ width: 212, background: '#3973bf' }}>
                      <span style={{ ...F, fontSize: 16, fontWeight: 600, color: '#fafafa' }}>Öğrenci ID Kopyala</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex gap-3 justify-end mb-5">
            <button
              onClick={() => openGoalModal('student', String(student.id))}
              className="rounded-[12px] px-5 py-3 hover:opacity-90 flex items-center gap-2" style={{ background: '#59e56e' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="8" y1="3" x2="8" y2="13" /><line x1="3" y1="8" x2="13" y2="8" /></svg>
              <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#fff' }}>Hedef Ata</span>
            </button>
            <button
              onClick={() => setBulkGoalTarget({ type: 'student', id: String(student.id), name: `${student.name} ${student.surname}`.trim() })}
              className="rounded-[12px] px-5 py-3 hover:bg-green-50" style={{ border: '2px solid #59e56e' }}>
              <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#179e3b' }}>Toplu Hedef (.xlsx)</span>
            </button>
            <button
              onClick={() => student.uuid && setView('student-program')}
              className="rounded-[12px] px-5 py-3 hover:opacity-90 disabled:opacity-50"
              disabled={!student.uuid}
              style={{ background: '#3973bf' }}>
              <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#fafafa' }}>Programını İncele</span>
            </button>
            <button onClick={() => setView('student-perf')} className="rounded-[12px] px-5 py-3 hover:opacity-90" style={{ background: '#e5599b' }}>
              <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#fafafa' }}>Performans Raporu</span>
            </button>
          </div>
          {/* Medals */}
          <h2 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b', marginBottom: 12 }}>Madalyalar</h2>
          <div className="bg-white rounded-[26px] p-5">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['/medals/cards/master-beyin-tam-kapasite.png', '/medals/cards/gold-beyin-tam-kapasite.png', '/medals/cards/standard-bilim-aktif.png', '/medals/cards/standard-adaptasyon-basladi.png'].map((img, i) => (
                <div key={i} className="w-[162px] h-[250px] flex-shrink-0"><img src={img} alt="" className="w-full h-full object-cover rounded-[12px]" /></div>
              ))}
            </div>
          </div>
        </div>
        {/* Right: info fields */}
        <div className="w-full xl:w-[425px] xl:flex-shrink-0 space-y-3.5">
          {[{ l: 'Ad', v: student.name }, { l: 'Soyad', v: student.surname }, { l: 'Okul', v: student.school }, { l: 'Sınıf', v: student.grade }, { l: 'E-posta', v: student.email }, { l: 'Telefon', v: student.phone }].map((f, i) => (
            <div key={i} className="bg-white rounded-[24px] px-5 py-4 flex items-center gap-3" style={{ height: 69 }}>
              <PersonIcon />
              <div><p style={{ ...F, fontSize: 12, fontWeight: 500, color: '#3973bf' }}>{f.l}</p><p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{f.v}</p></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ---- CLASSROOM DETAIL ----
  if (view === 'detail') return (
    <div>
      {toast}{addStudentModal}{goalAssignModal}{bulkGoalModal}
      {removeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => !removeBusy && setRemoveTarget(null)} />
          <div className="relative bg-white rounded-[20px] w-full max-w-[420px] p-6 text-center">
            <p style={{ ...F, fontSize: 17, fontWeight: 700, color: '#2b2b2b' }} className="mb-2">Öğrenciyi sınıftan kaldır</p>
            <p style={{ ...F, fontSize: 14, color: '#666' }} className="mb-5">
              <strong>{removeTarget.name}</strong> adlı öğrenciyi sınıftan kaldırmak istediğinize emin misiniz?
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setRemoveTarget(null)} disabled={removeBusy} className="rounded-[12px] px-6 py-2.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
                <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>Vazgeç</span>
              </button>
              <button onClick={doRemoveStudent} disabled={removeBusy} className="rounded-[12px] px-6 py-2.5 hover:opacity-90 disabled:opacity-50" style={{ background: '#e5599b' }}>
                <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>{removeBusy ? 'Kaldırılıyor…' : 'Kaldır'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Codes modal must be rendered in EVERY view that exposes a "Manage codes"
          button — `setCodesFor()` only updates state, but if the modal JSX
          isn't mounted under the current view's return, nothing appears. */}
      {codesFor && (
        <TeacherClassCodesModal
          classId={codesFor.id}
          className={codesFor.name}
          onClose={() => setCodesFor(null)}
        />
      )}
      <CreateAssignmentModal open={createHwOpen} onClose={() => setCreateHwOpen(false)} />
      <div className="flex items-center gap-3 mb-5">
        <BackBtn onClick={() => setView('list')} />
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Öğrenciler - {cls.students.length}</h1>
      </div>
      {/* Class header card — compact: smaller cover + right-sized title so it
          doesn't dominate the page. */}
      <div className="bg-[#fafafa] rounded-[20px] flex overflow-hidden mb-4" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
        <div className="w-[130px] xl:w-[200px] flex-shrink-0 overflow-hidden">
          <img src={cls.cover} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 p-4 xl:p-5 flex flex-col justify-center gap-1 min-w-0">
          <p className="truncate" style={{ ...F, fontSize: 20, fontWeight: 700, color: '#2b2b2b' }}>{cls.institution}</p>
          <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#888' }}>{cls.classCode} - {cls.students.length} Öğrenci</p>
          <div className="mt-2">
            <button onClick={() => setCodesFor({ id: cls.id, name: cls.classCode })} className="rounded-[10px] px-4 py-2 hover:bg-blue-50" style={{ border: '2px solid #3973bf' }}>
              <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#3973bf' }}>Sınıf Kodlarını Yönet</span>
            </button>
          </div>
        </div>
      </div>

      {/* Below card: student search + action buttons. flex-wrap + nowrap labels
          so buttons keep single-line text and drop to a second row instead of
          squishing into a cramped 2-line block. */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative" style={{ flex: '0 1 260px' }}>
          <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input
            value={studentQuery}
            onChange={e => setStudentQuery(e.target.value)}
            placeholder="Öğrenci ara"
            className="w-full rounded-[12px] border border-gray-200 pl-10 pr-3 py-2.5 outline-none focus:border-[#3973bf]"
            style={{ ...F, fontSize: 14 }}
          />
        </div>
        <div className="flex flex-wrap gap-2 ml-auto">
          <button onClick={() => openGoalModal('class', String(cls.id))}
            className="rounded-[12px] px-4 py-2.5 flex items-center gap-2 hover:opacity-90 whitespace-nowrap" style={{ background: '#59e56e' }}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="8" y1="3" x2="8" y2="13" /><line x1="3" y1="8" x2="13" y2="8" /></svg>
            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>Sınıfa Hedef Ata</span>
          </button>
          <button onClick={() => setBulkGoalTarget({ type: 'class', id: String(cls.id), name: cls.classCode })}
            className="rounded-[12px] px-4 py-2.5 flex items-center gap-2 hover:bg-green-50 whitespace-nowrap" style={{ border: '2px solid #59e56e' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#179e3b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#179e3b' }}>Toplu Hedef (.xlsx)</span>
          </button>
          <button onClick={() => openAddStudent(cls.id)} className="rounded-[12px] px-4 py-2.5 flex items-center gap-2 hover:bg-blue-50 whitespace-nowrap" style={{ border: '2px solid #3973bf' }}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="#3973bf" strokeWidth="2"><line x1="8" y1="3" x2="8" y2="13" /><line x1="3" y1="8" x2="13" y2="8" /></svg>
            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#3973bf' }}>Öğrenci Ekle</span>
          </button>
          <button onClick={() => setCreateHwOpen(true)} className="rounded-[12px] px-4 py-2.5 flex items-center gap-2 hover:bg-pink-50 whitespace-nowrap" style={{ border: '2px solid #e5599b' }}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="#e5599b" strokeWidth="2"><line x1="8" y1="3" x2="8" y2="13" /><line x1="3" y1="8" x2="13" y2="8" /></svg>
            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#e5599b' }}>Ödev Oluştur</span>
          </button>
          <button onClick={() => setView('class-perf')} className="rounded-[12px] px-4 py-2.5 hover:opacity-90 whitespace-nowrap" style={{ background: '#e5599b' }}>
            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fafafa' }}>Sınıf Performansı</span>
          </button>
        </div>
      </div>
      {/* Leave-request banner (also shown on the list view) */}
      {leaveRequestsBanner}
      {/* Student grid — full-width cards inside the grid (no fixed width, so they
          never overlap), each with its own remove (×) button. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
        {(() => {
          const q = studentQuery.trim().toLocaleLowerCase('tr');
          const list = q ? cls.students.filter(s => `${s.name} ${s.surname}`.toLocaleLowerCase('tr').includes(q)) : cls.students;
          if (list.length === 0) return <p className="col-span-full text-gray-400 text-sm text-center py-8">Öğrenci bulunamadı.</p>;
          return list.map(s => (
          <div key={s.id} onClick={() => { setSelectedStudentId(s.id); setView('student-profile'); }}
            className="bg-[#fafafa] rounded-[20px] p-4 flex flex-col items-center cursor-pointer hover:shadow-md transition-shadow relative w-full" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            {s.enrollmentId && (
              <button
                onClick={e => { e.stopPropagation(); setRemoveTarget({ enrollmentId: s.enrollmentId!, name: `${s.name} ${s.surname}`.trim() }); }}
                title="Sınıftan kaldır"
                className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-red-500 transition-colors"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
              </button>
            )}
            <div className="w-[64px] h-[64px] rounded-full bg-[#3973bf] overflow-hidden mb-3 mt-1"><img src="/a-avatar.png" alt="" className="w-full h-full object-cover" /></div>
            <p className="text-center leading-tight" style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>{s.name} {s.surname}</p>
            {s.leaveRequested && (
              <span className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
                style={{ ...F, background: '#fff4e0', border: '1px solid #f0b34a', fontSize: 12, fontWeight: 600, color: '#b3730a' }}>
                Ayrılmak istiyor
              </span>
            )}
          </div>
          ));
        })()}
      </div>
    </div>
  );

  // ---- LIST VIEW ----
  return (
    <div>
      {toast}{addStudentModal}{addClassModal}{joinClassModal}
      {codesFor && (
        <TeacherClassCodesModal
          classId={codesFor.id}
          className={codesFor.name}
          onClose={() => setCodesFor(null)}
        />
      )}
      {/* Old modal pattern removed — programlı view is now an inline
          sub-view triggered by setView('student-program'). The programFor
          state is kept for backwards compat but no longer renders. */}
      <CreateAssignmentModal open={createHwOpen} onClose={() => setCreateHwOpen(false)} />
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-4">
          <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Sınıflarım</h1>
          {quota && (
            <div className="flex gap-2 text-[12px]" style={{ ...F }}>
              <span className={`px-2.5 py-1 rounded-full font-semibold ${quota.class_count >= quota.class_limit ? 'bg-[#fdecef] text-[#c73d74]' : 'bg-[#eaf1ff] text-[#3973bf]'}`}>
                {quota.class_count}/{quota.class_limit} sınıf
              </span>
              <span className={`px-2.5 py-1 rounded-full font-semibold ${quota.student_count >= quota.student_limit ? 'bg-[#fdecef] text-[#c73d74]' : 'bg-[#eaf1ff] text-[#3973bf]'}`}>
                {quota.student_count}/{quota.student_limit} öğrenci
              </span>
              <span className={`px-2.5 py-1 rounded-full font-semibold ${quota.active_code_count >= quota.active_code_limit ? 'bg-[#fdecef] text-[#c73d74]' : 'bg-[#eaf1ff] text-[#3973bf]'}`}>
                {quota.active_code_count}/{quota.active_code_limit} kod
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowAddClass(true)} className="rounded-[12px] px-5 py-3 flex items-center gap-2 hover:opacity-90" style={{ background: '#e5599b' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#fafafa" strokeWidth="2"><line x1="9" y1="3" x2="9" y2="15" /><line x1="3" y1="9" x2="15" y2="9" /></svg>
            <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Sınıf Oluştur</span>
          </button>
          <button onClick={() => { setJoinError(null); setJoinSuccess(null); setShowJoinClass(true); }} className="rounded-[12px] px-5 py-3 flex items-center gap-2 hover:opacity-90" style={{ background: '#6d79ff' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fafafa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>
            <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Sınıfa Katıl</span>
          </button>
          <button onClick={() => openAddStudent()} className="rounded-[12px] px-5 py-3 flex items-center gap-2 hover:opacity-90" style={{ background: '#3973bf' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#fafafa" strokeWidth="2"><line x1="9" y1="3" x2="9" y2="15" /><line x1="3" y1="9" x2="15" y2="9" /></svg>
            <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Öğrenci Ekle</span>
          </button>
        </div>
      </div>
      {leaveRequestsBanner}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : classrooms.length === 0 ? (
        <p className="text-center text-gray-400 py-16">Henüz sınıfınız yok. "+ Sınıf Ekle" ile oluşturun.</p>
      ) : (
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {classrooms.map(c => (
          <div key={c.id} onClick={() => { setSelectedClassId(c.id); setView('detail'); }}
            className="bg-[#fafafa] rounded-[16px] overflow-hidden cursor-pointer hover:shadow-md transition-shadow" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <div className="h-[106px] overflow-hidden"><img src={c.cover} alt="" className="w-full h-full object-cover object-center" /></div>
            <div className="p-4">
              <p style={{ ...F, fontSize: 20, fontWeight: 500, color: '#2b2b2b' }}>{c.institution}</p>
              <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b', marginTop: 4 }}>{c.classCode}</p>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-1.5"><PersonIcon /><span style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{c.students.length} Öğrenci</span></div>
                <button
                  onClick={(e) => { e.stopPropagation(); setCodesFor({ id: c.id, name: c.classCode }); }}
                  className="text-[12px] font-semibold rounded-[8px] px-3 py-1.5 hover:opacity-90"
                  style={{ ...F, background: '#3973bf', color: '#fff' }}
                >
                  Kodlar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

export default TeacherClasses;
